/**
 * Public Courtroom Watch — khán giả xem livestream + gửi câu hỏi Q&A.
 * Không cần auth. Route: /public/courtroom/:courtroomId
 *
 * Hiển thị:
 *  - Embed Daily room (nếu livestream_enabled)
 *  - Form gửi câu hỏi (rate-limit từ BE)
 *  - Danh sách câu hỏi đã duyệt (approved) — realtime qua polling 15s
 *
 * Note: BE expose courtroom public info ở /public/courtrooms/{id} chưa có,
 * page này tự lấy meeting_link từ query param hoặc env defaults. Câu hỏi
 * dùng /courtrooms/public/{id}/qa (đã có endpoint công khai).
 */
import { useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { QuestionAnswer, Send } from '@mui/icons-material'

import {
  useSubmitPublicQuestionMutation,
  useListApprovedPublicQuestionsQuery,
} from '../store/api/publicQaApi'

export default function PublicCourtroomWatchPage() {
  const { courtroomId = '' } = useParams<{ courtroomId: string }>()
  const [search] = useSearchParams()
  // BE chưa expose public courtroom info → cho phép truyền meeting_link qua
  // query để embed (BTC chia link dạng /public/courtroom/{id}?embed=<daily_url>).
  const embedUrl = search.get('embed') || ''
  const title = search.get('title') || 'Phiên xử công khai'

  const [guestName, setGuestName] = useState('')
  const [question, setQuestion] = useState('')
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitSuccess, setSubmitSuccess] = useState(false)

  const [submitQ, { isLoading: submitting }] = useSubmitPublicQuestionMutation()
  const {
    data: approved = [],
    refetch,
    isLoading,
  } = useListApprovedPublicQuestionsQuery(courtroomId, {
    skip: !courtroomId,
    pollingInterval: 15_000, // Refresh nhẹ mỗi 15s
  })

  // Local persistence cho guestName
  useEffect(() => {
    const saved = window.localStorage.getItem('public_qa_guest_name')
    if (saved) setGuestName(saved)
  }, [])
  useEffect(() => {
    if (guestName.trim()) {
      window.localStorage.setItem('public_qa_guest_name', guestName.trim())
    }
  }, [guestName])

  const handleSubmit = async () => {
    setSubmitError(null)
    setSubmitSuccess(false)
    if (!question.trim() || question.trim().length < 5) {
      setSubmitError('Câu hỏi quá ngắn (tối thiểu 5 ký tự).')
      return
    }
    try {
      await submitQ({
        courtroomId,
        question: question.trim(),
        guest_name: guestName.trim() || undefined,
      }).unwrap()
      setSubmitSuccess(true)
      setQuestion('')
      // Approved list sẽ tự update khi moderator duyệt — polling sẽ pick up.
    } catch (e: any) {
      setSubmitError(e?.data?.detail || e?.message || 'Gửi câu hỏi thất bại')
    }
  }

  if (!courtroomId) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">Thiếu courtroomId trong URL.</Alert>
      </Container>
    )
  }

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 3 }, mb: 4, px: { xs: 2, sm: 3 } }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Phiên xử đang được phát trực tiếp. Bạn có thể gửi câu hỏi để Chủ tọa /
        BTC duyệt và hiển thị trong phiên.
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gap: 3,
          gridTemplateColumns: { xs: '1fr', md: '1.4fr 1fr' },
        }}
      >
        {/* Livestream embed */}
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary">
              Livestream
            </Typography>
          </Box>
          {embedUrl ? (
            <Box sx={{ position: 'relative', paddingTop: '56.25%' }}>
              <Box
                component="iframe"
                src={embedUrl}
                allow="camera; microphone; fullscreen; speaker; display-capture"
                sx={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  border: 0,
                }}
              />
            </Box>
          ) : (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Livestream chưa khả dụng. Hãy kiểm tra link BTC chia sẻ —
                URL phải có tham số <code>?embed=&lt;daily_url&gt;</code>.
              </Typography>
            </Box>
          )}
        </Paper>

        {/* Q&A column */}
        <Stack spacing={2}>
          {/* Submit form */}
          <Paper variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
              <QuestionAnswer fontSize="small" color="primary" />
              <Typography variant="subtitle1" fontWeight={700}>
                Gửi câu hỏi
              </Typography>
            </Stack>
            <Stack spacing={1.5}>
              <TextField
                fullWidth
                size="small"
                label="Tên hiển thị (tuỳ chọn)"
                value={guestName}
                onChange={(e) => setGuestName(e.target.value)}
                placeholder="Khán giả"
              />
              <TextField
                fullWidth
                multiline
                minRows={3}
                size="small"
                label="Câu hỏi"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder="VD: Đội Nguyên đơn căn cứ Điều luật nào để yêu cầu...?"
              />
              {submitError && <Alert severity="error">{submitError}</Alert>}
              {submitSuccess && (
                <Alert severity="success">
                  Đã gửi. Câu hỏi sẽ xuất hiện công khai khi Chủ tọa duyệt.
                </Alert>
              )}
              <Button
                variant="contained"
                startIcon={submitting ? <CircularProgress size={16} /> : <Send />}
                onClick={handleSubmit}
                disabled={submitting || question.trim().length < 5}
              >
                Gửi câu hỏi
              </Button>
            </Stack>
          </Paper>

          {/* Approved questions */}
          <Paper variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
              <Typography variant="subtitle1" fontWeight={700}>
                Câu hỏi đã được Chủ tọa duyệt
              </Typography>
              <Chip
                size="small"
                label={`${approved.length}`}
                variant="outlined"
                sx={{ ml: 'auto' }}
              />
              <Button size="small" onClick={() => refetch()}>
                Làm mới
              </Button>
            </Stack>
            {isLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={20} />
              </Box>
            )}
            {!isLoading && approved.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                Chưa có câu hỏi nào được duyệt.
              </Typography>
            )}
            <Stack spacing={1}>
              {approved.map((q) => (
                <Card key={q.id} variant="outlined" sx={{ borderRadius: 1 }}>
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                      <Chip
                        size="small"
                        label={q.guest_name || 'Khán giả'}
                        variant="outlined"
                        sx={{ height: 20, fontSize: 11 }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {q.moderated_at
                          ? new Date(q.moderated_at).toLocaleString('vi-VN')
                          : ''}
                      </Typography>
                    </Stack>
                    <Typography
                      variant="body2"
                      sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                    >
                      {q.question}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </Paper>
        </Stack>
      </Box>
    </Container>
  )
}
